import { type IOption, range } from "@components/ui-kit";
import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type ProjectOurWorkItemsQuery,
  ProjectTimeBreakdownDocument,
  type ProjectTimeBreakdownQuery,
  type ProjectTimeBreakdownQueryVariables,
  SortOrder,
  SqlOperator,
  type TimeTrackingProjectReport,
  TimeTrackingProjectReportOrderByColumnEnum,
  type TimeTrackingProjectReportQuery,
  TimeTrackingProjectReportsWhereColumn,
  type TimeTrackingProjectReportsWhereConditions,
  TimeTrackingReportsWhereColumn,
} from "@src/__generated__/graphql";
import {
  createTimeReportUrl,
  type TimeReportFilter,
} from "@src/components/modules/time-tracking/Report/time-report-url";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { appStore } from "@src/stores/AppStore";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { getCommonRanges, type TRanges } from "@src/utils/date-ranges";
import { toApiDate } from "@src/utils/dates";
import { NumberModel } from "@src/utils/models";
import { omitEmpty, type DateRange as Range } from "@src/utils/types";
import { addMonths } from "date-fns";
import { FieldState } from "formstate";
import { compact } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { LinkProps } from "next/link";
import { ProjectBreakdownGroupModel } from "./models";

export class Store {
  projectId?: string;
  @observable project: ProjectOurWorkItemsQuery["project"];
  @observable breakdownGroups: ProjectBreakdownGroupModel[] = [];

  monthlyBreakdownFetcher = new FetchHelper<
    ProjectTimeBreakdownQuery,
    ProjectTimeBreakdownQueryVariables
  >(ProjectTimeBreakdownDocument);

  form = new Form();

  orderBy = new OrderBy<TimeTrackingProjectReportOrderByColumnEnum>([
    {
      column: TimeTrackingProjectReportOrderByColumnEnum.UserFullName,
      order: SortOrder.Desc,
    },
  ]);

  constructor() {
    makeObservable(this);
  }

  @computed get monthlyBreakdownSelected() {
    return this.orderBy.clauses.length === 0;
  }

  @computed get activeOrderByColumn() {
    if (this.orderBy.clauses.length > 1) return null;
    return this.orderBy.clauses[0];
  }

  @computed get asWhereParam(): TimeTrackingProjectReportsWhereConditions {
    const { budgetItem, range, custom } = this.form.fields;
    const dateRange = getCommonRanges().find((i) => i.value === range.value)!;

    // compute right range from selected ranges or custom range
    const bi = budgetItem.value === "all" ? [] : [budgetItem.value];
    const r =
      range.value === "custom"
        ? [toApiDate(custom.value.start), toApiDate(custom.value.end)]
        : range.value === "all-time"
          ? []
          : dateRange.raw
            ? [toApiDate(dateRange.raw.start), toApiDate(dateRange.raw.end)]
            : [];

    return {
      AND: compact([
        omitEmpty({
          column: TimeTrackingProjectReportsWhereColumn.OurWorkBudgetItemId,
          value: bi,
          operator: SqlOperator.In,
        }),
        omitEmpty({
          column: TimeTrackingProjectReportsWhereColumn.TrackedForDate,
          value: r,
          operator: SqlOperator.Between,
        }),
      ]),
    };
  }

  @observable.ref reports: TimeTrackingModel[] = [];

  fromTimeTrackingProjectReportQuery(src: TimeTrackingProjectReportQuery) {
    this.reports =
      src.timeTrackingProjectReport?.map((i) => new TimeTrackingModel(i!)) ??
      [];
  }

  fromProjectOurWorkItemsQuery(src: ProjectOurWorkItemsQuery) {
    this.project = src.project;
  }

  @computed get brandId() {
    return this.project?.brand.id;
  }

  @computed get projectFound() {
    return !!this.project;
  }

  @computed get projectUsesBudgetovac() {
    return this.project?.uses_budgetovac;
  }

  @computed get projectIsFinished() {
    return this.project?.finished_at;
  }

  @computed get ourWorkOptions(): IOption[] {
    const options: IOption[] = [{ label: t`All budget items`, value: "all" }];

    if (this?.project) {
      this.project.ourWork.forEach((budgetItem) => {
        options.push({
          label: budgetItem.title,
          value: budgetItem.id,
        });
      });
    }

    return options;
  }

  @computed get breakdownTotalTrackedTime() {
    return this.breakdownGroups.reduce(
      (prev, { tracked_time }) => prev + tracked_time,
      0,
    );
  }

  @computed get isTimeBillingEnabled() {
    return Boolean(
      appStore.workspaceStore.settings?.moduleSettings.time_billing,
    );
  }

  @computed get reportTotalAmount() {
    return this.reports.reduce(
      (prev, report) =>
        prev +
        (this.isTimeBillingEnabled
          ? (report.invoiced_amount ?? 0)
          : (report.amount ?? 0)),
      0,
    );
  }

  @computed get reportTotalProfitability() {
    return this.reports.reduce(
      (prev, report) =>
        prev +
        (this.isTimeBillingEnabled
          ? (report.invoiced_personal_profitability?.denominatedValue ?? 0)
          : (report.personal_profitability?.denominatedValue ?? 0)),
      0,
    );
  }

  @computed get breakdownTotalAmount() {
    return this.breakdownGroups.reduce(
      (prev, { tracked_amount }) => prev + tracked_amount,
      0,
    );
  }

  @computed get breakdownTotalBudget() {
    return this.breakdownGroups.reduce(
      (prev, { budgeted }) => prev + budgeted,
      0,
    );
  }

  @computed get breakdownTotalPersonalExpense() {
    return this.breakdownGroups.reduce(
      (prev, { personal_expenses }) => prev + personal_expenses,
      0,
    );
  }

  @computed get breakdownTotalProfit() {
    return this.breakdownGroups.reduce((prev, { profit }) => prev + profit, 0);
  }

  getUrl = (add?: TimeReportFilter[]): LinkProps["href"] => {
    let dateRange: [string, string] | undefined;
    const dateRangeOption = getCommonRanges().find(
      (r) => r.value === this.form.fields.range.$,
    )!;

    if (dateRangeOption.value === "custom") {
      dateRange = [
        toApiDate(this.form.fields.custom.$.start),
        toApiDate(this.form.fields.custom.$.end),
      ];
    } else if (dateRangeOption.value === "all-time" && !!this.project) {
      dateRange = [
        this.project.start_date,
        this.project?.finished_at ?? toApiDate(addMonths(new Date(), 1)),
      ];
    } else if (dateRangeOption.raw) {
      dateRange = [
        toApiDate(dateRangeOption.raw.start),
        toApiDate(dateRangeOption.raw.end),
      ];
    }

    const additionalFilters: TimeReportFilter[] = [];

    if (this.form.fields.budgetItem.value !== "all") {
      additionalFilters.push({
        column: TimeTrackingReportsWhereColumn.OurWorkBudgetItemId,
        operator: SqlOperator.In,
        value: [this.form.fields.budgetItem.value],
      });
    }

    if (add?.length) {
      additionalFilters.push(...add);
    }

    return createTimeReportUrl({
      projectId: this.projectId!,
      brandId: this.brandId,
      dateRange,
      additionalFilters: additionalFilters.length
        ? additionalFilters
        : undefined,
    });
  };

  @action.bound handleMonthlyBreakdownSelect() {
    this.orderBy.setClauses([]);
    this.fetchMonthlyBreakdown();
  }

  @action.bound async fetchMonthlyBreakdown() {
    if (!this.projectId) {
      captureEvent({
        message: "Project ID is not set.",
      });
      appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    const [data, error] = await this.monthlyBreakdownFetcher.fetch({
      projectId: this.projectId,
    });

    if (error) return;
    this.breakdownGroups = data.projectTimeBreakdowns.map(
      (breakdown) => new ProjectBreakdownGroupModel(breakdown, appStore),
    );
  }

  @action.bound handleOrderChange(orderedBy: {
    column: TimeTrackingProjectReportOrderByColumnEnum;
    order: SortOrder | undefined;
  }) {
    if (!orderedBy.order) {
      this.orderBy.setClauses([]);
      return;
    }

    this.orderBy.setClauses([
      {
        column: orderedBy.column,
        order: orderedBy.order,
      },
    ]);
  }

  static create() {
    return new Store();
  }

  @computed
  get activeOrderByColumnLabel(): string {
    if (this.monthlyBreakdownSelected) {
      return t`Time breakdown`;
    }

    switch (this.activeOrderByColumn?.column) {
      case TimeTrackingProjectReportOrderByColumnEnum.UserFullName:
        return t`Users`;
      case TimeTrackingProjectReportOrderByColumnEnum.WorkType:
        return t`Positions`;
      case TimeTrackingProjectReportOrderByColumnEnum.OurWorkBudgetItemTitle:
        return t`Budget items`;
      default:
        return t`Time breakdown`;
    }
  }
}

export class TimeTrackingModel {
  user_full_name?: string | null;
  tracked_time: number;
  amount: number;
  invoiced_amount: number;
  our_work_budget_item_title?: string | null;
  hourly_rate: number;
  internal_hourly_rate: number;
  time_tracking_work_type_title?: string | null;
  group_tracked_time: number;
  group_amount: number;
  image?: string | null;
  user_id?: string | null;
  our_work_budget_item_id?: string | null;
  time_tracking_work_type_id?: string | null;
  team_id?: string | null;
  personal_profitability?: NumberModel;
  invoiced_personal_profitability?: NumberModel;
  personal_expense?: NumberModel;
  billable_all?: TimeTrackingProjectReport["billable_all"];
  invoiced_seconds: number;
  overserviced_seconds: number;
  uninvoiced_seconds: number;

  constructor(src: TimeTrackingProjectReport) {
    this.user_full_name = src.user_full_name;
    this.tracked_time = src.tracked_time;
    this.amount = src.amount;
    this.invoiced_amount = src.invoiced_amount;
    this.hourly_rate = src.hourly_rate ?? 0;
    this.internal_hourly_rate = src.internal_hourly_rate ?? 0;
    this.our_work_budget_item_title = src.our_work_budget_item_title;
    this.time_tracking_work_type_title = src.time_tracking_work_type_title;
    this.group_tracked_time = src.group_tracked_time;
    this.group_amount = src.group_amount;
    this.image = src.image;
    this.user_id = src.user_id;
    this.our_work_budget_item_id = src.our_work_budget_item_id;
    this.time_tracking_work_type_id = src.time_tracking_work_type_id;
    this.team_id = src.team_id;
    this.personal_profitability = src.personal_profitability
      ? NumberModel.fromDenomValue(
          src.personal_profitability,
          appStore.workspaceStore.settings?.currency.denomination!,
        )
      : undefined;
    this.invoiced_personal_profitability = src.invoiced_personal_profitability
      ? NumberModel.fromDenomValue(
          src.invoiced_personal_profitability,
          appStore.workspaceStore.settings?.currency.denomination!,
        )
      : undefined;
    this.personal_expense = src.personal_expense
      ? NumberModel.fromDenomValue(
          src.personal_expense,
          appStore.workspaceStore.settings?.currency.denomination!,
        )
      : undefined;
    this.billable_all = src.billable_all;
    this.invoiced_seconds = Number(src.invoiced_seconds ?? 0);
    this.overserviced_seconds = Number(src.overserviced_seconds ?? 0);
    this.uninvoiced_seconds = Number(src.uninvoiced_seconds ?? 0);
  }
}

class Form {
  fields = {
    budgetItem: new FieldState("all"),
    range: new FieldState<TRanges>("all-time"),
    custom: new FieldState<Range>(range()),
  };
}
